import React from "react";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { Briefcase, Calendar, MapPin, ChevronRight } from "lucide-react";
import { experiences } from "../../data/mock";

const ExperienceSection = () => {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.15 });

  return (
    <section id="experience" className="py-24 sm:py-32 relative" ref={ref} aria-label="Work Experience">
      <div className="max-w-[1200px] mx-auto px-6">
        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="text-sm font-semibold text-[rgb(218,255,1)] uppercase tracking-widest">
            Experience
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mt-3 tracking-tight">
            Where I've{" "}
            <span className="text-[rgb(218,255,1)]">worked</span>
          </h2>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-0 md:left-8 top-0 bottom-0 w-px bg-[rgb(63,63,63)]" />

          <div className="flex flex-col gap-8">
            {experiences.map((exp, index) => (
              <motion.div
                key={exp.id}
                initial={{ opacity: 0, x: -30 }}
                animate={inView ? { opacity: 1, x: 0 } : {}}
                transition={{ duration: 0.5, delay: 0.2 * index }}
                className="relative pl-8 md:pl-20"
              >
                {/* Timeline dot */}
                <div
                  className={`absolute left-0 md:left-8 top-6 w-3 h-3 rounded-full -translate-x-1.5 border-2 ${
                    exp.type === "current"
                      ? "bg-[rgb(218,255,1)] border-[rgb(218,255,1)] shadow-[0_0_12px_rgba(218,255,1,0.4)]"
                      : "bg-[rgb(38,40,42)] border-[rgb(63,63,63)]"
                  }`}
                />

                {/* Card */}
                <div className="group p-6 sm:p-8 rounded-2xl bg-[rgb(26,28,30)] border border-[rgba(255,255,255,0.1)] hover:border-[rgb(218,255,1)]/30 transition-all duration-300">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 mb-4">
                    <div>
                      <h3 className="text-xl font-semibold text-white">
                        {exp.role}
                      </h3>
                      <div className="flex items-center gap-2 mt-1">
                        <Briefcase size={14} className="text-[rgb(218,255,1)]" />
                        <span className="text-[rgb(218,218,218)] text-sm font-medium">
                          {exp.company}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Calendar size={14} className="text-[rgb(161,161,170)]" />
                      <span className="text-sm text-[rgb(161,161,170)]">
                        {exp.duration}
                      </span>
                      {exp.type === "current" && (
                        <span className="ml-2 px-2.5 py-0.5 text-xs font-semibold bg-[rgba(218,255,1,0.1)] text-[rgb(218,255,1)] rounded-full border border-[rgba(218,255,1,0.2)]">
                          Current
                        </span>
                      )}
                    </div>
                  </div>

                  <ul className="space-y-3">
                    {exp.responsibilities.map((resp, rIndex) => (
                      <li key={rIndex} className="flex items-start gap-3">
                        <ChevronRight
                          size={16}
                          className="text-[rgb(218,255,1)] mt-0.5 flex-shrink-0"
                        />
                        <span className="text-[rgb(218,218,218)] text-sm leading-relaxed">
                          {resp}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* Tech stack tags for freelance */}
                  {exp.techStack && exp.techStack.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-[rgba(255,255,255,0.06)]">
                      {exp.techStack.map((tech) => (
                        <span
                          key={tech}
                          className="px-3 py-1.5 text-xs font-medium text-[rgb(218,218,218)] bg-[rgb(38,40,42)] rounded-lg border border-[rgba(255,255,255,0.05)] hover:border-[rgba(218,255,1,0.3)] hover:text-[rgb(218,255,1)] transition-colors duration-200"
                        >
                          {tech}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ExperienceSection;
